// @flow

import * as React from 'react';
import { FolderFilled, FolderOpenFilled } from '@ant-design/icons';
import { Tree } from 'antd';
import styles from './Menu.css';
import { setDraggedItem, getDraggedItem, clearDraggedItem } from '../dragState';

const transformDirectory = name => {
  const map = {
    'Method Statement': 'Health & Safety',
    'Risk Assessment': 'Quality Control',
    'Planswift Take Off': 'Grayscales',
  }
  return map[name] || name
}

type Props = {
  rootDirectories: [],
  currentDirectoryPath: null|string,
  onSelectRootDirectory: (path: string) => void,
  onDragDropItem?: (sourcePath: string, sourceType: string, destinationPath: string) => void,
}

export default class extends React.PureComponent<Props> {

  state = {
    expandedKeys: [],
    dragOverNodeKey: null
  }

  componentDidMount() {
    // Auto-expand to show current directory
    this.expandToCurrentPath();
  }

  componentDidUpdate(prevProps) {
    // If current directory changed, expand to show it
    if (prevProps.currentDirectoryPath !== this.props.currentDirectoryPath) {
      this.expandToCurrentPath();
    }
  }

  expandToCurrentPath = () => {
    const { currentDirectoryPath, rootDirectories } = this.props;

    if (!currentDirectoryPath) return;

    // Get all parent paths that need to be expanded
    const pathsToExpand = [];
    this.collectParentPaths(rootDirectories, currentDirectoryPath, pathsToExpand);

    // Merge with existing expanded keys to keep other folders open
    this.setState(prevState => ({
      expandedKeys: [...new Set([...prevState.expandedKeys, ...pathsToExpand])]
    }));
  }

  collectParentPaths = (nodes, targetPath, pathsToExpand) => {
    for (const node of nodes) {
      if (targetPath.startsWith(node.path)) {
        pathsToExpand.push(node.path);
        if (node.children && node.children.length > 0) {
          this.collectParentPaths(node.children, targetPath, pathsToExpand);
        }
      }
    }
  }

  convertToTreeData = (directories) => {
    return directories.map(directory => ({
      title: (
        <span
          data-drop-path={directory.path}
          onDragOver={(e) => {
            // Check if this is a drag from the file list
            const types = e.dataTransfer.types || [];
            const hasSourcePath = types.includes('sourcepath') || types.includes('sourcePath');

            if (hasSourcePath) {
              e.preventDefault();
              e.stopPropagation();
              e.dataTransfer.dropEffect = 'move';
              this.setState({ dragOverNodeKey: directory.path });
            }
          }}
          onDrop={(e) => {
            const sourcePath = e.dataTransfer.getData('sourcePath');
            const sourceType = e.dataTransfer.getData('sourceType');

            if (sourcePath && sourceType) {
              e.preventDefault();
              e.stopPropagation();

              console.log('Tree node drop:', { sourcePath, sourceType, destination: directory.path });

              // Prevent dropping a folder into itself or its children
              if (sourceType === 'directory' && directory.path.startsWith(sourcePath)) {
                this.setState({ dragOverNodeKey: null });
                return;
              }

              if (this.props.onDragDropItem) {
                this.props.onDragDropItem(sourcePath, sourceType, directory.path);
              }

              this.setState({ dragOverNodeKey: null });
            }
          }}
          onDragLeave={() => {
            this.setState({ dragOverNodeKey: null });
          }}
          style={{
            display: 'inline-block',
            padding: '2px 4px',
            borderRadius: '2px',
            backgroundColor: this.state.dragOverNodeKey === directory.path ? '#e6f7ff' : 'transparent',
            border: this.state.dragOverNodeKey === directory.path ? '1px dashed #1890ff' : '1px solid transparent',
            transition: 'all 0.2s'
          }}
        >
          {transformDirectory(directory.name)}
        </span>
      ),
      key: directory.path,
      icon: ({ expanded }) => expanded ? <FolderOpenFilled /> : <FolderFilled />,
      children: directory.children && directory.children.length > 0
        ? this.convertToTreeData(directory.children)
        : undefined
    }));
  }

  onExpand = (expandedKeys) => {
    this.setState({ expandedKeys });
  }

  onSelect = (selectedKeys) => {
    if (selectedKeys.length > 0) {
      this.props.onSelectRootDirectory(selectedKeys[0]);
    }
  }

  allowDrop = () => {
    // Allow dropping on all tree nodes (all are directories)
    return true;
  }

  handleDragOver = (e) => {
    // Check if this is a drag from the file list
    const types = e.dataTransfer.types || [];
    const hasSourcePath = types.includes('sourcepath') || types.includes('sourcePath');

    if (hasSourcePath) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
    }
  }

  handleDrop = (e) => {
    const { onDragDropItem } = this.props;
    const { dragOverNodeKey } = this.state;

    if (!onDragDropItem) return;

    const draggedItem = getDraggedItem();
    const sourcePath = e.dataTransfer.getData('sourcePath') || (draggedItem ? draggedItem.path : '');
    const sourceType = e.dataTransfer.getData('sourceType') || (draggedItem ? draggedItem.type : '');

    console.log('Menu handleDrop:', { sourcePath, sourceType, dragOverNodeKey });

    if (!sourcePath || !sourceType) return;

    e.preventDefault();
    e.stopPropagation();

    // Use the actual drop target first, then the last hovered node, then the project root
    const dropKey = this.findDropPath(e.target) || dragOverNodeKey || this.getProjectRootPath();

    console.log('Drop target:', dropKey);

    if (dropKey) {
      // Prevent dropping a folder into itself or its children
      if (sourceType === 'directory' && dropKey.startsWith(sourcePath)) {
        this.setState({ dragOverNodeKey: null });
        return;
      }

      onDragDropItem(sourcePath, sourceType, dropKey);
    }

    clearDraggedItem();
    this.setState({ dragOverNodeKey: null });
  }

  findDropPath = (element) => {
    // Walk up the DOM to find the drop path
    let current = element;
    while (current && current !== document.body) {
      const dropPath = current.getAttribute('data-drop-path');
      if (dropPath) return dropPath;
      current = current.parentElement;
    }
    return null;
  }

  getProjectRootPath = () => {
    const { currentDirectoryPath, rootDirectories } = this.props;

    if (currentDirectoryPath) {
      return currentDirectoryPath.split('/').slice(0, 2).join('/');
    }

    if (rootDirectories && rootDirectories.length > 0) {
      return rootDirectories[0].path.split('/').slice(0, 2).join('/');
    }

    return null;
  }

  onDrop = (info) => {
    const { onDragDropItem } = this.props;

    if (!onDragDropItem) return;

    const dropKey = info.node.key; // destination path

    // Check if this is a drag from the tree or from the file list
    if (info.dragNode) {
      // Drag from tree
      const dragKey = info.dragNode.key; // source path

      // Prevent dropping a folder into itself or its children
      if (dropKey.startsWith(dragKey)) {
        return;
      }

      // Determine source type (always directory for tree nodes)
      const sourceType = 'directory';

      onDragDropItem(dragKey, sourceType, dropKey);

      // Stop propagation so the wrapper's handleDrop doesn't also fire for this drop
      if (info.event && info.event.stopPropagation) {
        info.event.stopPropagation();
      }
    } else if (info.event && info.event.dataTransfer) {
      // Drag from file list - check dataTransfer for source info
      const event = info.event;
      const draggedItem = getDraggedItem();
      const sourcePath = event.dataTransfer.getData('sourcePath') || (draggedItem ? draggedItem.path : '');
      const sourceType = event.dataTransfer.getData('sourceType') || (draggedItem ? draggedItem.type : '');

      if (sourcePath && sourceType) {
        // Prevent dropping a folder into itself or its children
        if (sourceType === 'directory' && dropKey.startsWith(sourcePath)) {
          return;
        }

        onDragDropItem(sourcePath, sourceType, dropKey);

        // Stop propagation so the wrapper's handleDrop doesn't also fire for this drop
        if (info.event && info.event.stopPropagation) {
          info.event.stopPropagation();
        }
      }
    }

    clearDraggedItem();
  }

  onDragStart = (info) => {
    // Track tree-internal drags so the wrapper fallback can also move them to the root
    if (info && info.node && info.node.key) {
      setDraggedItem(info.node.key, 'directory');
    }
  }

  onDragEnd = () => {
    clearDraggedItem();
  }

  render () {

    const {
      rootDirectories,
      currentDirectoryPath,
      onDragDropItem
    } = this.props;

    const { expandedKeys } = this.state;

    const treeData = this.convertToTreeData(rootDirectories);
    const projectRootPath = this.getProjectRootPath();

    return (
      <div
        className={styles.wrapper}
        data-drop-path={projectRootPath}
        onDragOver={this.handleDragOver}
        onDrop={this.handleDrop}
      >

        <div className={styles.heading}>Folders</div>

        <Tree
          showIcon
          draggable={!!onDragDropItem}
          allowDrop={this.allowDrop}
          selectedKeys={currentDirectoryPath ? [currentDirectoryPath] : []}
          expandedKeys={expandedKeys}
          onExpand={this.onExpand}
          onSelect={this.onSelect}
          onDrop={this.onDrop}
          onDragStart={this.onDragStart}
          onDragEnd={this.onDragEnd}
          treeData={treeData}
          style={{ paddingTop: 30, paddingBottom: 30 }}
          className={styles.tree}
        />

      </div>
    );
  }
}
